import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { COLORS } from '../constants/colors.ts';
import { GROOM_NAME, BRIDE_NAME } from '../constants/event.ts';

interface WelcomeOverlayProps {
  onOpenInvitation: () => void;
}

const WelcomeOverlay: React.FC<WelcomeOverlayProps> = ({ onOpenInvitation }) => {
  // Prevent scrolling when overlay is active (including mobile)
  useEffect(() => {
    // Save current styles
    const originalBodyStyle = window.getComputedStyle(document.body).overflow;
    const originalHtmlStyle = window.getComputedStyle(document.documentElement).overflow;
    const originalBodyPosition = document.body.style.position;
    const originalBodyTop = document.body.style.top;
    const originalBodyWidth = document.body.style.width;
    
    // Get current scroll position
    const scrollY = window.scrollY;
    
    // Prevent scrolling on both body and html
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    
    // Fix body position to prevent iOS Safari bounce
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    
    // Prevent touch events that could cause scrolling
    const preventTouchMove = (e: TouchEvent) => {
      // Allow touch events on the overlay itself and its children
      const target = e.target as Element;
      const overlayElement = document.querySelector('[data-overlay="welcome"]');
      
      if (overlayElement && (overlayElement.contains(target) || overlayElement === target)) {
        return; // Allow touch events within the overlay
      }
      
      e.preventDefault();
    };
    
    // Add touch event listeners
    document.addEventListener('touchmove', preventTouchMove, { passive: false });
    document.addEventListener('touchstart', preventTouchMove, { passive: false });
    
    // Cleanup function
    return () => {
      // Restore original styles
      document.body.style.overflow = originalBodyStyle;
      document.documentElement.style.overflow = originalHtmlStyle;
      document.body.style.position = originalBodyPosition;
      document.body.style.top = originalBodyTop;
      document.body.style.width = originalBodyWidth;
      
      // Remove touch event listeners
      document.removeEventListener('touchmove', preventTouchMove);
      document.removeEventListener('touchstart', preventTouchMove);
      
      // Restore scroll position
      window.scrollTo(0, scrollY);
    };
  }, []);

  return (
    <motion.div 
      data-overlay="welcome"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 z-[10001] flex flex-col items-center justify-between p-0 h-[100lvh] w-full"
      style={{ backgroundColor: COLORS.primary }}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.8 }}
        className="flex flex-col items-center justify-center flex-grow w-full px-6 sm:px-10 pb-8"
      >
        <div className="text-center max-w-md mx-auto">
          {/* Decorative top — line + diamond */}
          <motion.div 
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5, ease: "easeOut" }}
            className="flex items-center justify-center gap-3 mb-12"
          >
            <div className="h-px w-12 sm:w-16" style={{ backgroundColor: `${COLORS.accent}99` }} />
            <div className="w-1.5 h-1.5 rotate-45" style={{ backgroundColor: COLORS.accent }} />
            <div className="h-px w-12 sm:w-16" style={{ backgroundColor: `${COLORS.accent}99` }} />
          </motion.div>
          
          {/* Greeting */}
          <motion.div
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-10"
          >
            <p className="font-optima text-xs sm:text-sm tracking-[0.25em] uppercase leading-relaxed" style={{ color: `${COLORS.accent}CC` }}>
              Welcome to the wedding reception of
            </p>
            <div className="space-y-8">
              <motion.h1 
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.95, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="font-alex-brush text-4xl sm:text-5xl font-semibold leading-tight" 
                style={{ color: COLORS.accent }}
              >
                {GROOM_NAME}
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.1, duration: 0.4 }}
                className="font-optima text-2xl italic" 
                style={{ color: `${COLORS.accent}DD` }}
              >
                &amp;
              </motion.p>
              <motion.h1 
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.2, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="font-alex-brush text-4xl sm:text-5xl font-semibold leading-tight" 
                style={{ color: COLORS.accent }}
              >
                {BRIDE_NAME}
              </motion.h1>
            </div>
          </motion.div>

          {/* Decorative divider — line + diamond */}
          <motion.div 
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 1.35, duration: 0.5, ease: "easeOut" }}
            className="flex items-center justify-center gap-3 my-12"
          >
            <div className="h-px w-12 sm:w-16" style={{ backgroundColor: `${COLORS.accent}99` }} />
            <div className="w-1.5 h-1.5 rotate-45" style={{ backgroundColor: COLORS.accent }} />
            <div className="h-px w-12 sm:w-16" style={{ backgroundColor: `${COLORS.accent}99` }} />
          </motion.div>

          {/* Button */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-xs mx-auto"
          >
            <motion.button
              onClick={onOpenInvitation}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full font-optima font-medium px-10 py-3.5 rounded-full transition-all duration-300 tracking-[0.2em] uppercase border-2"
              style={{ 
                backgroundColor: 'transparent',
                color: COLORS.accent,
                borderColor: COLORS.accent
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${COLORS.accent}22`;
                e.currentTarget.style.borderColor = COLORS.accent;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = COLORS.accent;
              }}
            >
              Open Card
            </motion.button>
          </motion.div>
        </div>
      </motion.div>

      {/* Safe area spacing at bottom */}
      <div className="w-full pb-8 sm:pb-16" style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom))' }} />
    </motion.div>
  );
};

export default WelcomeOverlay;
